// Signup form — Google + email/password + invisible Turnstile + Firebase
// error mapping. Extracted from SignupPage 2026-07-30 (PLAN_GHL_MVP.md P1
// addendum) so SignupPage and GhlInstallPage share one form. Behavior must
// stay identical for SignupPage.
//
// The form owns auth + the idempotent POST /signup/create-account call; the
// host page owns what happens next via onComplete (navigation / claiming).

import { useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import {
  GoogleAuthProvider,
  signInWithPopup,
  createUserWithEmailAndPassword,
  sendEmailVerification,
} from 'firebase/auth';
import { Loader2, Check } from 'lucide-react';
import { Turnstile, TurnstileInstance } from '@marsidev/react-turnstile';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { PasswordInput } from '@/components/ui/password-input';
import { Label } from '@/components/ui/label';
import { auth } from '@/lib/firebase';
import { apiRequest } from '@/lib/api';

const TURNSTILE_SITE_KEY = import.meta.env.VITE_TURNSTILE_SITE_KEY ?? '';

const signupSchema = z.object({
  email: z.email('Please enter a valid email'),
  password: z.string().min(8, 'Password must be at least 8 characters'),
});
type SignupFormData = z.infer<typeof signupSchema>;

const firebaseErrorMessages: Record<string, string> = {
  'auth/email-already-in-use': 'An account with this email already exists. Sign in instead?',
  'auth/weak-password': 'Password is too weak. Try at least 8 characters.',
  'auth/invalid-email': "That doesn't look like a valid email.",
  'auth/network-request-failed': "Couldn't reach the server. Check your connection and try again.",
  'auth/popup-closed-by-user': 'Google sign-in was cancelled. Try again?',
  'auth/popup-blocked': 'Your browser blocked the Google sign-in popup. Allow popups and try again.',
};

function getErrorMessage(err: unknown): string {
  const code = (err as { code?: string })?.code;
  if (code && firebaseErrorMessages[code]) return firebaseErrorMessages[code];
  const message = (err as { message?: string })?.message;
  return message || 'Something went wrong. Try again in a moment.';
}

const googleProvider = new GoogleAuthProvider();

interface SignupFormProps {
  /** Called after auth + create-account complete. Host decides navigation. */
  onComplete: () => void | Promise<void>;
  /** Prefill for the email field (e.g. installer email from the CRM — a hint,
   *  never an auth key; Google/password still provide the proof). */
  prefillEmail?: string | null;
  submitLabel?: string;
  /** Activation contexts (GhlInstallPage): render the Google button bigger
   *  and heavier — it's the primary action there. Default keeps SignupPage
   *  rendering identical. */
  emphasizeGoogle?: boolean;
}

export function SignupForm({
  onComplete,
  prefillEmail,
  submitLabel = 'Start tracking now',
  emphasizeGoogle = false,
}: SignupFormProps) {
  const [loading, setLoading] = useState(false);
  const [authError, setAuthError] = useState<string>('');
  const [turnstileToken, setTurnstileToken] = useState<string>('');
  const turnstileRef = useRef<TurnstileInstance | null>(null);

  const {
    register,
    handleSubmit,
    watch,
    formState: { errors, isSubmitting },
  } = useForm<SignupFormData>({
    resolver: zodResolver(signupSchema),
    defaultValues: { email: prefillEmail ?? '', password: '' },
  });

  const passwordValue = watch('password') ?? '';
  const passwordLength = passwordValue.length;
  const passwordOk = passwordLength >= 8;

  // After Firebase auth completes, create the users row (idempotent backend),
  // force-refresh the ID token, then hand control back to the host page.
  async function postAuthFlow(extraBody?: Record<string, unknown>) {
    try {
      await apiRequest<{ client_id: string | null; status: 'new' | 'existing' }>(
        '/signup/create-account',
        { method: 'POST', body: extraBody },
      );
      if (auth.currentUser) await auth.currentUser.getIdToken(true);
    } catch (err) {
       
      console.warn('signup/create-account failed:', err);
    }
    await onComplete();
  }

  async function handleGoogleSignup() {
    setAuthError('');
    setLoading(true);
    try {
      await signInWithPopup(auth, googleProvider);
      await postAuthFlow();
    } catch (err) {
      setAuthError(getErrorMessage(err));
    } finally {
      setLoading(false);
    }
  }

  async function handleEmailSignup(data: SignupFormData) {
    setAuthError('');
    if (TURNSTILE_SITE_KEY && !turnstileToken) {
      setAuthError("Couldn't verify you're human. Refresh the page and try again.");
      return;
    }
    try {
      const cred = await createUserWithEmailAndPassword(auth, data.email, data.password);
      // Soft email verification: fire-and-forget. Never block signup if
      // the email send fails — the user can resend from the dashboard banner.
      sendEmailVerification(cred.user, {
        url: 'https://app.heymotek.com/',
      }).catch((err) => {
         
        console.warn('sendEmailVerification failed:', err);
      });
      await postAuthFlow(
        turnstileToken ? { turnstile_token: turnstileToken } : undefined,
      );
    } catch (err) {
      setAuthError(getErrorMessage(err));
      // Token is single-use — reset the widget so a retry gets a fresh one.
      turnstileRef.current?.reset();
      setTurnstileToken('');
    }
  }

  const submitting = loading || isSubmitting;

  return (
    <>
      {/* Google */}
      <Button
        type="button"
        variant="outline"
        className={
          emphasizeGoogle
            ? 'w-full h-14 text-base font-bold mb-5 shadow-card border-2'
            : 'w-full h-12 text-base mb-5'
        }
        onClick={handleGoogleSignup}
        disabled={submitting}
      >
        {submitting ? (
          <Loader2 className="mr-2 h-5 w-5 animate-spin" />
        ) : (
          <svg width="20" height="20" viewBox="0 0 48 48" aria-hidden className="mr-2">
            <path fill="#FFC107" d="M43.611 20.083H42V20H24v8h11.303c-1.649 4.657-6.08 8-11.303 8-6.627 0-12-5.373-12-12s5.373-12 12-12c3.059 0 5.842 1.154 7.961 3.039l5.657-5.657C34.046 6.053 29.268 4 24 4 12.955 4 4 12.955 4 24s8.955 20 20 20 20-8.955 20-20c0-1.341-.138-2.65-.389-3.917z" />
            <path fill="#FF3D00" d="M6.306 14.691l6.571 4.819C14.655 15.108 18.961 12 24 12c3.059 0 5.842 1.154 7.961 3.039l5.657-5.657C34.046 6.053 29.268 4 24 4 16.318 4 9.656 8.337 6.306 14.691z" />
            <path fill="#4CAF50" d="M24 44c5.166 0 9.86-1.977 13.409-5.192l-6.19-5.238A11.91 11.91 0 0 1 24 36c-5.202 0-9.619-3.317-11.283-7.946l-6.522 5.025C9.505 39.556 16.227 44 24 44z" />
            <path fill="#1976D2" d="M43.611 20.083H42V20H24v8h11.303a12.04 12.04 0 0 1-4.087 5.571l.003-.002 6.19 5.238C36.971 39.205 44 34 44 24c0-1.341-.138-2.65-.389-3.917z" />
          </svg>
        )}
        Sign up with Google
      </Button>

      {/* OR divider */}
      <div className="flex items-center gap-4 mb-3 text-muted-foreground text-xs uppercase tracking-wider font-semibold">
        <div className="flex-1 h-px bg-dark/10" />
        <span>OR</span>
        <div className="flex-1 h-px bg-dark/10" />
      </div>

      {/* Email/password form */}
      <form onSubmit={handleSubmit(handleEmailSignup)} className="space-y-3 mt-3" noValidate>
        <div>
          <Label htmlFor="signup-email" className="sr-only">Email</Label>
          <Input
            id="signup-email"
            type="email"
            autoComplete="email"
            placeholder="Your email"
            className="h-12 rounded-xl"
            {...register('email')}
            aria-invalid={!!errors.email}
            aria-describedby={errors.email ? 'signup-email-error' : undefined}
          />
          {errors.email && (
            <p id="signup-email-error" role="alert" className="mt-1 text-xs text-destructive">
              {errors.email.message}
            </p>
          )}
        </div>

        <div>
          <Label htmlFor="signup-password" className="sr-only">Password</Label>
          <PasswordInput
            id="signup-password"
            autoComplete="new-password"
            placeholder="Your password (8+ characters)"
            className="h-12 rounded-xl"
            {...register('password')}
            aria-invalid={!!errors.password}
            aria-describedby={errors.password ? 'signup-password-error' : undefined}
          />
          {errors.password ? (
            <p id="signup-password-error" role="alert" className="mt-1 text-xs text-destructive">
              {errors.password.message}
            </p>
          ) : passwordLength > 0 ? (
            <p
              id="signup-password-hint"
              aria-live="polite"
              className={`mt-1 flex items-center gap-1 text-xs ${
                passwordOk ? 'text-emerald-600' : 'text-muted-foreground'
              }`}
            >
              {passwordOk ? (
                <>
                  <Check className="h-3 w-3" strokeWidth={3} />
                  Strong enough
                </>
              ) : (
                `${passwordLength} / 8 characters`
              )}
            </p>
          ) : null}
        </div>

        {/* Cloudflare Turnstile — invisible bot challenge. Fires on mount,
            sets token via onSuccess. Skipped when site key is unset. */}
        {TURNSTILE_SITE_KEY && (
          <Turnstile
            ref={turnstileRef}
            siteKey={TURNSTILE_SITE_KEY}
            onSuccess={setTurnstileToken}
            options={{ size: 'invisible' }}
          />
        )}

        {/* Auth-flow error (Firebase) */}
        {authError && (
          <p role="alert" aria-live="polite" className="text-sm text-destructive">
            {authError}
          </p>
        )}

        {/* Primary CTA */}
        <Button
          type="submit"
          className="w-full h-12 text-base font-bold mt-2 shadow-card"
          disabled={submitting}
        >
          {submitting ? (
            <>
              <Loader2 className="mr-2 h-5 w-5 animate-spin" />
              Creating account...
            </>
          ) : (
            submitLabel
          )}
        </Button>
      </form>
    </>
  );
}
